"use client";

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { DepartmentListPanel } from "../components/DepartmentListPanel";
import { PermissionListPanel } from "../components/PermissionListPanel";
import { permissionDepartmentService } from "../services/permission-department-service";
import { Department, Permission } from "../types";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";

// --- Main Component ---
const PermissionDepartmentIndexPage: React.FC = () => {
  // === STATE ===
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(
    null
  );
  const [originalPermissions, setOriginalPermissions] = useState<Set<number>>(new Set());
  const [stagedPermissions, setStagedPermissions] = useState<Set<number>>(new Set());
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [isLoadingAllPermissions, setIsLoadingAllPermissions] = useState(false);
  const [isLoadingDepartmentPermissions, setIsLoadingDepartmentPermissions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [departmentSearch, setDepartmentSearch] = useState("");
  const [permissionSearch, setPermissionSearch] = useState("");
  const { fetch: fetchList, loading: loadingList } = permissionDepartmentService.list();
  const { fetch: fetchListPermission, loading: loadingListPermission } = permissionDepartmentService.listPermission();
  const { fetch: fetchListPermissionsForDepartment, loading: loadingPermissionsForDepartment } = permissionDepartmentService.getPermissionsForDepartment();
  const { fetch: updatePermissionsForDepartment, loading: updatingPermissionsForDepartment } = permissionDepartmentService.updatePermissionsForDepartment();

  const listRef = useRef(fetchList);
  useEffect(() => { listRef.current = fetchList; }, [fetchList]);
  const listPermissionRef = useRef(fetchListPermission);
  useEffect(() => { listPermissionRef.current = fetchListPermission; }, [fetchListPermission]);

  // === DATA FETCHING ===
  const loadDepartments = useCallback(async () => {
    setIsLoadingDepartments(true);
    const res = await listRef.current();
    if (res && isSuccess(res)) {
        const apiData = Array.isArray(res.data) ? res.data : [];
      setAllDepartments(
        apiData.map((d: any) => ({
          id: d.id,
          name: d.name,
        }))
      );
    }
    setIsLoadingDepartments(false);
  }, []);
  
  const loadAllPermissions = useCallback(async () => {
    setIsLoadingAllPermissions(true);
    const res = await listPermissionRef.current();
    if (res && isSuccess(res)) {
        const apiData = Array.isArray(res.data) ? res.data : [];
      setAllPermissions(
        apiData.map((d: any) => ({ // d là một 'privilege' từ API
          id: d.id,
          code: d.code,
          name: d.name,
          description: d.description,
          // Lấy group name từ mảng privilege_group
          group: d.privilege_group && d.privilege_group.length > 0 ? d.privilege_group[0].name : 'Chưa phân loại',
        }))
      );
    }
    setIsLoadingAllPermissions(false);
  }, []);

  const filterDepartment = useMemo(() => {
    if (!departmentSearch) return allDepartments;
    return allDepartments.filter(d => d.name.toLowerCase().includes(departmentSearch.toLowerCase()));
  }, [allDepartments, departmentSearch]);

  useEffect(() => {
    void loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    void loadAllPermissions();
  }, [loadAllPermissions]);

  useEffect(() => {
    if (!selectedDepartment) {
      setOriginalPermissions(new Set());
      setStagedPermissions(new Set());
      return;
    }
    const loadDepartmentPerms = async () => {
      setIsLoadingDepartmentPermissions(true);
      const res = await fetchListPermissionsForDepartment(selectedDepartment.id);
      const apiData = (res && res.data && Array.isArray(res.data)) 
                    ? res.data 
                    : [];
      const permIds = apiData.flatMap(item => item.privileges);
      const permsSet = new Set(permIds);
      setOriginalPermissions(permsSet);
      setStagedPermissions(permsSet);
      setIsLoadingDepartmentPermissions(false);
    };
    void loadDepartmentPerms();
  }, [selectedDepartment]);
  
  // === AUTO-SELECT LOGIC ===
  useEffect(() => {
    if (isLoadingDepartments) {
      return;
    }
    if (allDepartments.length > 0) {
      const isSelectedValid =
        selectedDepartment &&
        allDepartments.some((d) => d.id === selectedDepartment.id);
      if (!isSelectedValid) {
        setSelectedDepartment(allDepartments[0]);
      }
    } else {
      setSelectedDepartment(null);
    }
  }, [allDepartments, isLoadingDepartments, selectedDepartment]);

  // === HANDLERS ===
  const handleSelectDepartment = (department: Department) => {
    setSelectedDepartment(department);
  };

  const handleTogglePermission = (permissionId: number) => {
    setStagedPermissions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permissionId)) {
        newSet.delete(permissionId);
      } else {
        newSet.add(permissionId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    if (!selectedDepartment) return;
    setIsSaving(true);
    const res = await updatePermissionsForDepartment(
      selectedDepartment.id,
      { privilege_ids: Array.from(stagedPermissions) }
    );
    if (res && isSuccess(res)) {
      setOriginalPermissions(stagedPermissions);
      showToast({ message: "Cập nhật quyền thành công", type: "success" });
    } else {
      // Có thể thêm toast báo lỗi ở đây
    }
    setIsSaving(false);
  };

  // === DERIVED STATE ===
  const isDirty = useMemo(() => {
    if (originalPermissions.size !== stagedPermissions.size) return true;
    for (const id of originalPermissions) {
      if (!stagedPermissions.has(id)) return true;
    }
    return false;
  }, [originalPermissions, stagedPermissions]);

  const sortedAllPermissions = useMemo(() => {
    // 1. Lọc danh sách quyền dựa trên từ khóa tìm kiếm
    const filteredPermissions = !permissionSearch
      ? allPermissions
      : allPermissions.filter(p =>
          (p.name || '').toLowerCase().includes(permissionSearch.toLowerCase()) ||
          (p.description || '').toLowerCase().includes(permissionSearch.toLowerCase())
        );

    // 2. Sắp xếp danh sách đã được lọc
    return [...filteredPermissions].sort((a, b) => {
      const groupA = a.group || ''; // Mặc định là chuỗi rỗng nếu không có group
      const groupB = b.group || ''; // Mặc định là chuỗi rỗng nếu không có group
      return groupA.localeCompare(groupB);
    });
  }, [allPermissions, permissionSearch]); // Thêm permissionSearch vào dependency

  return (
    <div className="flex flex-col lg:flex-row lg:h-screen w-full bg-slate-50 lg:overflow-hidden p-4 lg:p-0 gap-4 lg:gap-0">
      
      <DepartmentListPanel // <-- Use imported component
        departments={filterDepartment}
        isLoading={isLoadingDepartments}
        selectedDepartmentId={selectedDepartment?.id}
        onSelectDepartment={setSelectedDepartment}
        searchTerm={departmentSearch}
        onSearchChange={setDepartmentSearch}
      />

      <PermissionListPanel
        permissions={sortedAllPermissions}
        selectedItemName={selectedDepartment?.name}
        stagedPermissions={stagedPermissions}
        isLoadingAll={isLoadingAllPermissions}
        isLoadingItem={isLoadingDepartmentPermissions}
        isSaving={isSaving}
        isDirty={isDirty}
        onTogglePermission={handleTogglePermission}
        onSave={handleSave}
        searchTerm={permissionSearch}
        onSearchChange={setPermissionSearch}
      />

    </div>
  );
};

export default PermissionDepartmentIndexPage;